import { Check, Minus, ArrowRight, Eye, SlidersHorizontal, Users } from "lucide-react";
import Seo from "@/components/Seo";
import Reveal from "@/components/Reveal";
import {
    Container,
    Section,
    PageHero,
    AmberButton,
} from "@/components/Primitives";

const TIERS = [
    {
        name: "Observer",
        icon: Eye,
        tagline: "Watch the engine work, end to end.",
        highlight: false,
    },
    {
        name: "Operator",
        icon: SlidersHorizontal,
        tagline: "Run both engines with full replay.",
        highlight: true,
    },
    {
        name: "Desk",
        icon: Users,
        tagline: "A shared, auditable process for a small desk.",
        highlight: false,
    },
];

const ROWS = [
    { label: "Live dashboard (all stages)", values: [true, true, true] },
    { label: "Decision logs & reasoning", values: [true, true, true] },
    { label: "Stocks engine", values: ["View-only", true, true] },
    { label: "Futures engine", values: [false, true, true] },
    { label: "Backtest / replay mode", values: [false, true, true] },
    { label: "Concurrent tickers", values: ["TBD", "TBD", "TBD"] },
    { label: "Multi-seat / shared desk", values: [false, false, true] },
    { label: "Priority support", values: [false, false, true] },
];

const Cell = ({ v }) => {
    if (v === true)
        return <Check size={16} className="mx-auto text-signal" />;
    if (v === false)
        return <Minus size={16} className="mx-auto text-text-lo/50" />;
    return (
        <span className="font-mono text-xs text-text-hi">{v}</span>
    );
};

export default function Pricing() {
    return (
        <>
            <Seo
                title="Pricing"
                description="Simple tiers. No performance fees, no profit-share, no smoke. You pay for the tool, not a cut of outcomes we can't guarantee."
            />
            <PageHero
                eyebrow="Tiers"
                title="The standard doesn't change by tier. The access does."
                sub="No performance fees. No profit-share. No cut of outcomes we can't guarantee. You pay for the tool and the transparency."
            />

            <Section>
                <Container>
                    <Reveal>
                        <p className="mb-8 font-mono text-[11px] text-text-lo">
                            * Pricing and limits are placeholders for this
                            concept build. We set real, verifiable numbers
                            before launch — we don't invent figures.
                        </p>
                    </Reveal>

                    <div className="grid gap-5 md:grid-cols-3">
                        {TIERS.map((t, i) => (
                            <Reveal key={t.name} delay={i * 0.08}>
                                <div
                                    className={`flex h-full flex-col rounded-lg border p-7 ${
                                        t.highlight
                                            ? "border-rule bg-ink-soft"
                                            : "border-haze bg-ink-soft/60"
                                    }`}
                                >
                                    {t.highlight && (
                                        <span className="mb-4 inline-flex w-fit rounded-sm bg-rule px-2 py-0.5 font-mono text-[10px] uppercase tracking-wider text-ink">
                                            Most popular
                                        </span>
                                    )}
                                    <span
                                        className={`flex h-11 w-11 items-center justify-center rounded border ${
                                            t.highlight
                                                ? "border-rule/50 bg-rule/10 text-rule"
                                                : "border-haze bg-ink text-rule"
                                        }`}
                                    >
                                        <t.icon size={20} />
                                    </span>
                                    <h3 className="mt-5 text-lg font-semibold text-text-hi">
                                        {t.name}
                                    </h3>
                                    <p className="mt-1 text-sm text-text-lo">
                                        {t.tagline}
                                    </p>
                                    <div className="mt-6 flex items-baseline gap-2">
                                        <span className="inline-flex items-center rounded-sm border border-dashed border-text-lo/40 px-2.5 py-1 font-mono text-xs uppercase tracking-wider text-text-lo">
                                            Price TBD
                                        </span>
                                        <span className="font-mono text-sm text-text-lo">
                                            / mo
                                        </span>
                                    </div>
                                    <AmberButton
                                        to="/#early-access"
                                        testId={`pricing-cta-${t.name.toLowerCase()}`}
                                        className={`mt-7 w-full ${
                                            t.highlight ? "" : "!bg-haze !text-text-hi hover:!bg-rule hover:!text-ink"
                                        }`}
                                    >
                                        Get early access
                                    </AmberButton>
                                </div>
                            </Reveal>
                        ))}
                    </div>

                    {/* Comparison matrix */}
                    <Reveal>
                        <div className="mt-12 overflow-x-auto rounded-lg border border-haze">
                            <table className="w-full min-w-[560px] border-collapse text-sm">
                                <thead>
                                    <tr className="border-b border-haze bg-ink-soft">
                                        <th className="p-4 text-left font-medium text-text-lo">
                                            Features
                                        </th>
                                        {TIERS.map((t) => (
                                            <th
                                                key={t.name}
                                                className="p-4 text-center font-semibold text-text-hi"
                                            >
                                                {t.name}
                                            </th>
                                        ))}
                                    </tr>
                                </thead>
                                <tbody>
                                    {ROWS.map((r) => (
                                        <tr
                                            key={r.label}
                                            className="border-b border-haze/60 last:border-0"
                                        >
                                            <td className="p-4 text-text-lo">
                                                {r.label}
                                            </td>
                                            {r.values.map((v, idx) => (
                                                <td
                                                    key={idx}
                                                    className="p-4 text-center"
                                                >
                                                    <Cell v={v} />
                                                </td>
                                            ))}
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </Reveal>

                    <Reveal>
                        <p className="mt-8 text-base text-text-lo">
                            Every plan includes the same transparency.{" "}
                            <span className="text-text-hi">
                                We don't gate the &ldquo;why&rdquo; behind a
                                paywall.
                            </span>
                        </p>
                        <div className="mt-6">
                            <AmberButton to="/faq" testId="pricing-faq-cta">
                                Read common questions <ArrowRight size={16} />
                            </AmberButton>
                        </div>
                    </Reveal>
                </Container>
            </Section>
        </>
    );
}
